import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ClipboardList, Send } from 'lucide-react';
import { v3Api } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';

import TenantInformation from './forms/TenantInformation';
import DescriptionOfWork from './forms/DescriptionOfWork';
import ContractorDetails from './forms/ContractorDetails';
import ResponsiblePerson from './forms/ResponsiblePerson';
import WorkerDetails from './forms/WorkerDetails';
import PPEandSafetyEquipment from './forms/PPEandSafetyEquipment';
import OtherEquipment from './forms/OtherEquipment';
import GeneralDetails from './forms/GeneralDetails';
import CriticalWorkRequirements from './forms/CriticalWorkRequirements';
import VehicleDetails from './forms/VehicleDetails';
import './workpermit.css';

// componentMap keys MUST match the backend CANONICAL_COMPONENTS + template names.
const COMPONENT_MAP = {
    'Tenant information': TenantInformation,
    'Description of work': DescriptionOfWork,
    'Contractor details': ContractorDetails,
    'Responsible person': ResponsiblePerson,
    'Worker details': WorkerDetails,
    'PPE & safety equipment': PPEandSafetyEquipment,
    'Other equipment': OtherEquipment,
    'General details': GeneralDetails,
    'Critical work requirements': CriticalWorkRequirements,
    'Vehicle Details': VehicleDetails,
};
const NEEDS_CONTRACTORS = new Set(['Worker details', 'Responsible person']);

const NOC_TYPES = [
    'NOC for Employment', 'NOC for Visa Processing', 'NOC for Travel', 'NOC for Education',
    'NOC for Resignation', 'NOC for Internal Transfer', 'NOC for Passport Renewal',
    'NOC for Vehicle Registration', 'NOC for Bank Loan', 'NOC for Business Setup',
];

/** Map the dynamic formData sections → the backend WorkPermit schema shape. */
function buildPayload(formData, { type, workpermitType, nocType }) {
    const ti = formData['Tenant information'] || {};
    const dw = formData['Description of work'] || {};
    const gd = formData['General details'] || {};
    const cw = formData['Critical work requirements'] || {};
    const ppe = formData['PPE & safety equipment'] || {};
    const oeq = formData['Other equipment'] || {};
    const rp = (formData['Responsible person'] || [])[0] || {};

    const contractors = (formData['Contractor details'] || []).map((c) => ({
        name: c.ContractorName || '',
        company: c.ContractorAddress || '',
        number: c.ContractorMobile || '',
        email: c.ContractorEmail || '',
        LicenseAttachment: c.LicenseAttachment || '',
        license_number: c.LicenseNo || '',
        license_issue_date: c.LicenseIssueDate || null,
        license_expiry_date: c.LicenseExpiryDate || null,
    }));

    const workers = (formData['Worker details'] || []).map((w) => ({
        contractor: w.WorkerContractor || '',
        nationality: w.WorkerNationality || '',
        name: `${w.WorkerFirstName || ''} ${w.WorkerLastName || ''}`.trim(),
        email: w.WorkerEmail || '',
        number: w.WorkerContactNo || '',
        emiratesid: w.EmiratesIDNumber || '',
        emiratesid_expiry: w.EmiratesIDExpiry || '',
        emiratesid_attachment: w.EmiratesIDAttachment || '',
        passportno: w.PassportNumber || '',
        passport_issue: w.PassportIssue || '',
        passport_expiry: w.PassportExpiry || '',
        passport_attachment: w.PassportAttachment || '',
        visano: w.VisaNumber || '',
        visa_issue: w.VisaIssue || '',
        visa_expiry: w.VisaExpiry || '',
        visa_attachment: w.VisaAttachment || '',
        comments: w.WorkerComments || '',
    }));

    const vehicles = (formData['Vehicle Details'] || []).map((v) => ({
        vehicle_name: v.VehicleName || '',
        plate_number: v.PlateNumber || '',
        driver_name: v.DriverName || '',
        license_attachment: v.LicenseAttachment || '',
        emiratesid_attachment: v.EmiratesIDAttachment || '',
        material_details: v.MaterialDetails || '',
        date: v.Date || null,
        time_slot: v.TimeSlot || '',
        additional_info: v.AdditionalInfo || '',
    }));

    return {
        type,
        workpermit_type: type === 'NOC' ? nocType : workpermitType,
        nocType: type === 'NOC' ? nocType : '',
        tenant_information: {
            property: ti.selectedProperty || '',
            store: ti.selectedStore || '',
            unit_code: ti.selectedUnit || '',
            floor: ti.selectedFloor || '',
            applicant_name: ti.applicantName || '',
            designation: ti.designation || '',
            contact_number: ti.contactNumber || '',
            email: ti.emailAddress || '',
        },
        description_of_work: {
            duration: { from_date: dw.durationFrom || null, to_date: dw.durationTo || null, time: dw.durationTime || '' },
            method_of_statement: dw.methodStatementFile || '',
            risk_assessment: dw.riskAssessmentFile || '',
            drawings_file_path: dw.drawingsFile || '',
            additional_info: dw.additionalInfo || '',
        },
        general_details: { general_info: gd.generalInfo || '', pre_requisites: gd.pre_requisites || '' },
        contractor_details: contractors,
        responsible_person: {
            contractor: rp.PersonContractor || '',
            nationality: rp.PersonNationality || '',
            name: [rp.PersonFirstName, rp.PersonLastName].filter(Boolean).join(' '),
            email: rp.PersonEmail || '',
            number: rp.PersonContactNumber || '',
            EmiratesID: rp.PersonEmiratesID || '',
            ExpiryDate: rp.PersonEmiratesIDExpiry || '',
            Attachment: rp.PersonEmiratesIDAttachment || '',
            Comments: rp.PersonComments || '',
        },
        vehicle_details: vehicles,
        worker_details: workers,
        ppe_safety_equipment: {
            fire_extinguishers: ppe.fireExtinguisher || 'no',
            safety_helmet_shoes: ppe.safetyHelmetShoes || 'no',
            safety_vest: ppe.safetyVest || 'no',
            safety_harness: ppe.safetyHarness || 'no',
            ear_plugs_mask_gloves: ppe.earplugsMaskGloves || 'no',
            warning_signs_barriers: ppe.warningSignsBarriers || 'no',
            others: ppe.othersSafety || 'no',
            na: ppe.naSafety || 'no',
            description: ppe.description || '',
            attachments: ppe.attachments || [],
            selected_equipments: ppe.selectedEquipments || '',
        },
        other_equipment: {
            scaffolding: oeq.scaffolding || 'no',
            mobile_crane: oeq.mobileCrane || 'no',
            forklift: oeq.forklift || 'no',
            mewp: oeq.mewp || 'no',
            others: oeq.othersEquipment || 'no',
            na: oeq.naEquipment || 'no',
            comments: oeq.comments || '',
        },
        critical_work_requirements: {
            electric_isolation: !!cw.electricIsolation,
            gas_isolation: !!cw.gasIsolation,
            other_details: cw.otherDetails || '',
        },
    };
}

const statusClass = (s) => (s === 'APPROVED' ? 'approved' : s === 'REJECTED' ? 'rejected' : 'pending');

export default function WorkPermitApply() {
    const { t } = useTranslation();
    const { concessionaireId } = useAuth();

    const [templates, setTemplates] = useState([]);
    const [history, setHistory] = useState([]);
    const [type, setType] = useState('');
    const [workpermitType, setWorkpermitType] = useState('');
    const [nocType, setNocType] = useState('');
    const [componentList, setComponentList] = useState([]);
    const [formData, setFormData] = useState({});
    const [showPreview, setShowPreview] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const loadHistory = useCallback(() => {
        v3Api.workPermit.mine().then((rows) => setHistory(Array.isArray(rows) ? rows : [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!concessionaireId) return;
        v3Api.workPermit.templates().then((rows) => setTemplates(Array.isArray(rows) ? rows : [])).catch(() => {});
        loadHistory();
    }, [concessionaireId, loadHistory]);

    const handleTemplateChange = (name) => {
        setWorkpermitType(name);
        setComponentList([]);
        setFormData({});
        if (!name) return;
        v3Api.workPermit.componentByName(name)
            .then((tpl) => setComponentList((tpl.components || []).map((c) => c.name)))
            .catch(() => setComponentList([]));
    };

    const handleDataChange = useCallback((name, data) => {
        setFormData((prev) => ({ ...prev, [name]: data }));
    }, []);

    const showForm = (type === 'Workpermit' && workpermitType !== '') || (type === 'NOC' && nocType !== '');

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = buildPayload(formData, { type, workpermitType, nocType });
            await v3Api.workPermit.create(payload);
            toast.success(t('Work permit submitted successfully!'));
            setShowPreview(false);
            setFormData({});
            setComponentList([]);
            setType('');
            setWorkpermitType('');
            setNocType('');
            loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.message || t('Error submitting work permit.'));
        } finally {
            setSubmitting(false);
        }
    };

    const previewSections = useMemo(() => Object.entries(formData), [formData]);

    return (
        <div className="v3 v3-wp">
            <div className="v3-wp-header">
                <div>
                    <h1 className="v3-wp-title">{t('Work Permit / NOC')}</h1>
                    <div className="v3-wp-subtitle">{t('Submit a new request and track its approval status.')}</div>
                </div>
            </div>

            {/* Request history */}
            <div className="v3-wp-card">
                <div className="v3-wp-card-head"><h6 className="v3-wp-card-title"><ClipboardList size={16} /> {t('Request History')}</h6></div>
                <table className="v3-wp-table">
                    <thead>
                        <tr>
                            <th>{t('Type')}</th><th>{t('Applicant')}</th><th>{t('Created')}</th><th>{t('Status')}</th><th>{t('Approvals')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {history.length === 0 ? (
                            <tr><td colSpan="5" className="v3-wp-empty">{t('No work permits found')}</td></tr>
                        ) : history.map((p) => (
                            <tr key={p._id}>
                                <td>{p.workpermit_type}</td>
                                <td>{p.tenant_information?.applicant_name || '—'}</td>
                                <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                                <td><span className={`v3-wp-pill ${statusClass(p.status)}`}>{p.status}</span></td>
                                <td>
                                    {(p.multi_level_approvals || []).map((a, i) => (
                                        <div key={i} className="v3-wp-muted">{a.user_role}: {a.approved_status}</div>
                                    )) || '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* New request */}
            <div className="v3-wp-card">
                <div className="v3-wp-card-head"><h6 className="v3-wp-card-title">{t('New Request')}</h6></div>
                <div className="v3-wp-toolbar">
                    <div className="v3-wp-field">
                        <label className="v3-wp-label">{t('Type')}</label>
                        <div className="v3-wp-radios">
                            <label className={`v3-wp-radio${type === 'Workpermit' ? ' on' : ''}`}>
                                <input type="radio" name="wptype" checked={type === 'Workpermit'} onChange={() => { setType('Workpermit'); setNocType(''); setWorkpermitType(''); setComponentList([]); }} />
                                {t('Work Permit')}
                            </label>
                            <label className={`v3-wp-radio${type === 'NOC' ? ' on' : ''}`}>
                                <input type="radio" name="wptype" checked={type === 'NOC'} onChange={() => { setType('NOC'); setNocType(''); setWorkpermitType(''); setComponentList([]); }} />
                                {t('NOC')}
                            </label>
                        </div>
                    </div>

                    {type === 'Workpermit' && (
                        <div className="v3-wp-field">
                            <label className="v3-wp-label">{t('Work Permit Type')}</label>
                            <select className="v3-wp-select" value={workpermitType} onChange={(e) => handleTemplateChange(e.target.value)}>
                                <option value="">{t('Select Work Permit Type')}</option>
                                {templates.map((tpl) => <option key={tpl._id} value={tpl.name}>{tpl.name}</option>)}
                            </select>
                        </div>
                    )}

                    {type === 'NOC' && (
                        <div className="v3-wp-field">
                            <label className="v3-wp-label">{t('NOC Type')}</label>
                            <select className="v3-wp-select" value={nocType} onChange={(e) => setNocType(e.target.value)}>
                                <option value="">{t('Select NOC Type')}</option>
                                {NOC_TYPES.map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Dynamic sub-forms */}
            {showForm && (
                <>
                    {componentList.length === 0 ? (
                        <div className="v3-wp-card"><div className="v3-wp-muted">{t('No components configured for this type.')}</div></div>
                    ) : componentList.map((name) => {
                        const Comp = COMPONENT_MAP[name];
                        if (!Comp) return <div className="v3-wp-card" key={name}><div className="v3-wp-error">⚠️ {t('No component for')} "{name}"</div></div>;
                        const extra = NEEDS_CONTRACTORS.has(name) ? { contractors: formData['Contractor details'] || [] } : {};
                        return <Comp key={name} onDataChange={(d) => handleDataChange(name, d)} {...extra} />;
                    })}

                    <div className="v3-wp-actions" style={{ justifyContent: 'flex-end' }}>
                        <button className="v3-btn v3-btn-primary" onClick={() => setShowPreview(true)} disabled={componentList.length === 0}>
                            {t('Apply')}
                        </button>
                    </div>
                </>
            )}

            {/* Preview + submit */}
            {showPreview && (
                <div className="v3-wp-overlay" onClick={() => setShowPreview(false)}>
                    <div className="v3-wp-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="v3-wp-modal-head">
                            <h5 className="v3-wp-title" style={{ fontSize: 18 }}>{t('Preview of Submitted Fields')}</h5>
                            <button className="v3-wp-iconbtn" onClick={() => setShowPreview(false)}>✕</button>
                        </div>
                        {previewSections.map(([section, val]) => (
                            <div className="v3-wp-detail-section" key={section}>
                                <h6>{section}</h6>
                                <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: 'var(--v3-text-secondary)', margin: 0 }}>
                                    {JSON.stringify(val, null, 2)}
                                </pre>
                            </div>
                        ))}
                        <div className="v3-wp-rowfoot" style={{ marginTop: 18 }}>
                            <button className="v3-btn" onClick={() => setShowPreview(false)}>{t('Cancel')}</button>
                            <button className="v3-btn v3-btn-primary" onClick={submit} disabled={submitting}>
                                <Send size={14} /> {submitting ? t('Submitting…') : t('Submit')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
